<script setup lang="ts">
import SearchAttribute from "@/components/CvrConvert/SearchAttribute.vue"
import HeaderElement from '@/components/CvrConvert/VehiclesListParts/HeaderElement.vue'
import { Ref, ref, computed } from 'vue'
import i18n from '@/lang/i18n';

const emits = defineEmits(['update:modelValue', 'search'])

type propsType = {
  headerLabels: {
    id: string,
    labelPath: string,
    class: string,
    style: string,
    labelStyle: string,
    labelClass: string,
    searchable: boolean,
    sortable: boolean,
    titleWidth: string
  }[]
  modelValue: {[key: string]: string[]}
  items: {
    [key: string]: {id: string | number, key: string, label: string}[]
  }
  sortKey: string
  sortType: string
}
const props = withDefaults(defineProps<propsType>(),{
  sortKey: "",
  sortType: ""
})

type selectableItemType = {
  id: string | number;
  key: string;
  label: string;
}
const openWindowKey: Ref<string> = ref("")
const filteredStatus: Ref<{[key: string]: selectableItemType}> = ref({})
const searchedKeys: Ref<string[]> = ref([])
const selectedElement: Ref<{
  bottom: number,
  left: number,
  width: number
}> = ref({
  bottom: 0,
  left: 0,
  width: 0
})
const isAllSelectedByWindowKey: Ref<{[key: string]: boolean}> = ref({})

const modelValue = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emits("update:modelValue", value);
  },
});

function openSearchWindow(key: string, searchable: boolean) {
  if (!searchable || !key) {
    return
  }

  const selectedContent: HTMLElement | null = document.getElementById(key)
  const element = selectedContent?.getBoundingClientRect()
  if (!element) {
    return
  }

  openWindowKey.value = key
  filteredStatus.value = JSON.parse(JSON.stringify(modelValue.value[key]))
  selectedElement.value = {
    bottom: element.bottom + window.scrollY,
    left: element.left + window.scrollX,
    width: element.width
  }
}

function closeWindow(status: boolean) {
  if (!status) {
    modelValue.value[openWindowKey.value] = JSON.parse(JSON.stringify(filteredStatus.value))
    openWindowKey.value = ""
  }
}

function filterStatus() {
  openWindowKey.value = ""
  emits("search");
}

function setIsAllSelectedByWindowKey($event: { windowKey: string; isAllSelected: boolean; }) {
  isAllSelectedByWindowKey.value[$event.windowKey] = $event.isAllSelected;
}
</script>
<template>
  <thead>
    <tr>
      <template
        v-for="(header, index) in props.headerLabels"
        :key="index"
      >
        <th
          :id="header.id"
          :class="header.class"
          style="z-index: 1000"
          @click="openSearchWindow(header.id, header.searchable)"
        >
          <HeaderElement
            :title="i18n.global.t(header.labelPath)"
            :elementKey="header.id"
            :searchedKeys="searchedKeys"
            :labelStyle="header.labelStyle"
            :searchable="header.searchable"
            :sortable="header.sortable"
            :sortKey="props.sortKey"
            :sortType="props.sortType"
            :isAllSelected="isAllSelectedByWindowKey[header.id]"
            titleWidth="80%"
          />
            <Teleport to="body">
              <SearchAttribute
                v-show="header.id && header.id == openWindowKey"
                v-model="modelValue[header.id]"
                :items="props.items[header.id]"
                :label="i18n.global.t('pages.cvr_uploads.list.uploadStatus')"
                :style="`top: ${selectedElement.bottom}px; left: ${selectedElement.left}px;`"
                :width="selectedElement.width"
                :windowKey="header.id"
                class="search-window"
                @closeWindow="closeWindow"
                @search="filterStatus"
                @setIsAllSelected="setIsAllSelectedByWindowKey"
              />
            </Teleport>
        </th>
      </template>
    </tr>
  </thead>
</template>
<style scoped>
.search-window {
  position: absolute;
  z-index: 1000;
}
.searchable:hover {
  background-color: #dcdcdc;
  transition: 0.3s;
  cursor: pointer;
}
</style>
